import {Component, Listen, Prop, h, State} from '@stencil/core';
import { getChatActionSessionStorageValue } from '../../conversational-search-logic/storage-manager/storage-manager-helpers';
import type { labelProviderInterface } from '../../../../LabelProviderInterface';
import { conversationalSearchConnector } from '../../conversational-search-logic/connector/connector';
import dynamicBannerStore from './dynamic-banner-store';
import { delayLoadingViewUpdate, renderIntro, setIcon, setLongQueryTimeout } from './dynamic-banner-helpers';
import { renderVariant, renderHeaderTag } from './dynamic-banner-rendering';
import EventEmitterComponent from '../../conversational-search-partials/event-emitter-component/event-emitter-component';
import { isMobile } from '../../../../../utils/media';
import { Brands } from '../../../../../constants/brands';
import conversationalSearchProductsMap from '../../../../../utils/mappers/other/conversational-search-products-mapper';

@Component({
  tag: 'uiaas-search-widget-conversational-search-dynamic-banner',
  styleUrl: 'dynamic-banner.scss',
  shadow: true,
})
export class WidgetConversationalSearchDynamicBanner {
  @Prop() readonly labelProvider: labelProviderInterface;
  @Prop() readonly query: string;
  @Prop() readonly conversationalSearchProductsEndpoint: string;
  @Prop() readonly apigeeTokenUrl: string;
  @Prop() readonly apigeeAuth: string;
  @Prop() readonly language: string;
  @Prop() readonly country: string;
  @Prop() readonly conversationalSearchBrandUrl: string;
  @Prop() readonly conversationalSearchMedaliaFormId?: string;
  @Prop() readonly brand?: string;
  @Prop() readonly activeByDefault: boolean = false;

  @State() isActivated: boolean = false;
  @State() isProcessing: boolean = false;
  @State() isLongQuery = false;
  @State() isMobileView = isMobile();

  @Listen('resize', { target: 'window' })
  @Listen('load', { target: 'window' })
  handleResize() {
      this.isMobileView = isMobile();
  }

  private invoked = dynamicBannerStore.get('invoked');
  // eslint-disable-next-line
  public fetchTimeout: NodeJS.Timeout;
  // eslint-disable-next-line
  public loadingUpdateTimeout: NodeJS.Timeout;

  @Listen('guided:SearchCompleted', { target: 'window' })
  @Listen('guidedV2:SearchCompleted', { target: 'window' })
  handleReloadAndSubmitAction() {
      dynamicBannerStore.dispose();
  }

  @Listen('conversational-search:user-question-state', { target: 'window' })
  handlePendingRequest(e) {
      const processing = e.detail.isProcessing;
      if (processing) delayLoadingViewUpdate(this);
      else this.isProcessing = processing;
  }

  private async preFetch() {
      await dynamicBannerStore.set('invoked', true);
      this.invoked = true;
      setLongQueryTimeout(this);
  }

  private postFetch(responseData) {
      this.isProcessing = false;
      dynamicBannerStore.set('question', this.query);
      dynamicBannerStore.set('data', responseData);
      if (this.isLongQuery) this.isLongQuery = false;
      clearTimeout(this.fetchTimeout);
  }

  private async fetchData() {
      this.isActivated = !this.isActivated;
      const notInteracted = !getChatActionSessionStorageValue() && !this.invoked;

      if (notInteracted) {
        this.isProcessing = true;
        await this.preFetch();
        const responseData = await conversationalSearchConnector(
          this.apigeeTokenUrl,
          this.apigeeAuth,
          this.conversationalSearchProductsEndpoint,
          this.query,
          this.language,
          this.country,
        );

        if(this.brand === Brands.MYSCHNEIDER){
          responseData.results = conversationalSearchProductsMap(responseData.results);
        }

        this.postFetch(responseData);
      }
  }

  disconnectedCallback() {
      clearTimeout(this.fetchTimeout);
      clearTimeout(this.loadingUpdateTimeout);
  }


  componentWillLoad() {
      if(this.activeByDefault) {
        this.fetchData();
      }
  }
  render() {
      const resultsData = dynamicBannerStore.get('data');
      const question = dynamicBannerStore.get('question');
      const isFeedbackVisible = resultsData?.results?.length && this.isActivated && !this.isProcessing;
      const isInitialView = !getChatActionSessionStorageValue() && !this.invoked;

      return (
        <div class="conversational-search-banner-dynamic">
          <section
            class={{
              'conversational-search-banner-dynamic-main': true,
              'conversational-search-banner-dynamic-main--initial': isInitialView
            }}
          >
            <EventEmitterComponent
              eventName='conversational-search:ga-banner-interaction'
              keyword={question ?? this.query}
              action={this.isActivated ? 'fold' : 'unfold'}
            >
              <button class="conversational-search-banner-dynamic-main-header" onClick={() => this.fetchData()}>
                <div class="conversational-search-banner-dynamic-main-header__intro">
                  {renderHeaderTag(this.isProcessing, this.labelProvider, isInitialView, this.isMobileView)}
                  <qds-title layer="accessory" level="section" tag="h4">
                    {renderIntro(this.invoked, this.isLongQuery, this.isProcessing, this.labelProvider)}
                  </qds-title>
                </div>
                <qds-button
                  importance="subdued"
                  icon-name={this.isActivated ? 'chevron-up' : 'chevron-down'}
                  onClick={() => setIcon(this.isActivated)}
                  iconOnly={true}
                ></qds-button>
              </button>
            </EventEmitterComponent>
            {this.isActivated ? <section class='conversational-search-banner-dynamic-main-content'>
              {renderVariant(resultsData, this)}
            </section> : ''}
          </section>
          {this.isActivated && isFeedbackVisible ? <section class="conversational-search-banner-dynamic-footer">
            <uiaas-search-widget-conversational-search-dynamic-banner-feedback
              labelProvider={this.labelProvider}
              query={question ?? this.query}
              language={this.language}
              country={this.country}
              conversationalSearchMedaliaFormId={this.conversationalSearchMedaliaFormId}
            />
          </section> : ''}
        </div>
      );
  }
}
